<template>
  <v-row no-gutters class="pb-5 networkRow" align="center" style="height: 100%">
    <v-col cols="auto" class="pr-7 text-left color-select-name-column" v-if="!hideNameColumn">
      {{ `${label}:` }}
    </v-col>
    <v-col cols="auto" class="select-right align-center" style="height: 100%">
      <v-select
        ref="sel"
        class="select-color"
        :class="{ 'select-color--readonly': !isEditingOn }"
        v-on="$listeners"
        v-bind="{ ...$props, ...$attrs, ...staticProps }"
        :style="[selectStyle]"
        flat
        :loading="false"
        :disabled="$attrs.loading ? true : undefined"
        rounded
        :readonly="!isEditingOn"
        :append-icon="isEditingOn ? 'mdi-chevron-down' : ''"
        :clearable="isClearable"
        :dark="!$_isHexColorLight(selectStyle['--current-color']) ? true : false"
        :color="!$_isHexColorLight(selectStyle['--current-color']) ? '#fff' : '#202124'"
      >
        <template #selection v-if="$attrs.isAsync">{{ $attrs.value }}</template>
        <template #prepend-inner v-if="!$attrs.value">
          <span class="v-label no-data-label v-label--is-disabled theme--light">{{ noDataValue }}</span>
        </template>
        <template v-for="(value, key) in $scopedSlots" #[key]="scope">
          <slot :name="key" v-bind="scope" />
        </template>
        <template #item="{ item, on, attrs }">
          <v-list-item v-on="on" v-bind="attrs">
            <v-list-item-content>
              <v-list-item-title style="text-align: left">
                <v-chip
                  small
                  :color="computedColors[item.value ?? item].color"
                  class="tw:text-sm"
                  :class="{ 'white--text': !$_isHexColorLight(computedColors[item.value ?? item].color) }"
                  style="font-weight: 500; cursor: pointer"
                >
                  {{ item.text ?? item }}
                </v-chip>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
      </v-select>
    </v-col>
  </v-row>
</template>
<script>
export default {
  name: 'DataInputSelectcolor',
  props: {
    colors: {
      type: Array,
      required: true,
    },
    hideNameColumn: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
    },
    isEditingOn: {
      type: Boolean,
      default: false,
    },
    fontSize: {
      type: String,
      default: '14px',
    },
  },
  computed: {
    isClearable() {
      return [true, false].includes(this.$attrs.clearable) ? this.$attrs.clearable : this.isEditingOn;
    },
    computedColors() {
      const computedColors = {};
      this.$attrs.items.forEach((item, index) => {
        if (item.value && item.text) {
          computedColors[item.value] = {
            text: item.text,
            value: item.value,
            color: this.colors[index] || '#bababa',
          };
        } else {
          computedColors[item] = {
            color: this.colors[index] || '#bababa',
          };
        }
      });
      return computedColors;
    },
    selectStyle() {
      const color = this.computedColors[this.$attrs.value]?.color || '#bababa';
      return {
        '--current-color': this.$attrs.loading ? `${color}50` : color,
        '--main-color': this.$_isHexColorLight(color) ? '#202124' : 'white',
        'font-weight': 500,
      };
    },
  },
  data: vm => ({
    noDataValue: vm.$i18n.t('admin.noData'),
    staticProps: {
      hideDetails: true,
      solo: true,
      menuProps: {
        closeOnContentClick: true,
        offsetY: true,
      },
      label: '',
      color: 'rgba(0,0,0,0)',
      class: 'text-body-2',
    },
  }),
};
</script>
<style lang="scss" scoped>
.color-select-name-column {
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 16px;
  color: #202124;
}
.networkRow {
  width: 100%;
  display: grid;
  grid-template-columns: 40% auto 1fr;
  grid-template-areas: 'textLeft selectRight .';
}

.text-left {
  grid-area: textLeft;
  max-width: 10em;
}

.select-right {
  grid-area: selectRight;
  min-width: 5%;
}

:deep(.v-input--is-readonly .v-input__append-inner) {
  display: none !important;
}
:deep(.v-input__append-inner .v-input__icon--clear) {
  opacity: 1 !important;
  margin-left: 0 !important;
}
:deep(.select-color:not(.v-input--is-readonly) .v-select__selections) {
  margin-right: 0.25rem;
}

.select-color {
  height: 30px;
  max-height: 30px !important;
  ::v-deep .v-input__control .v-input__slot .v-select__slot .v-label {
    position: static !important;
    right: 0 !important;
    width: 100%;
  }
  ::v-deep .v-input__control .v-input__slot .v-select__slot .v-select__selections input {
    display: none !important;
  }
  ::v-deep .v-input__control .v-input__slot .v-input__prepend-inner {
    padding: 0 !important;
  }
  ::v-deep .v-input__control .v-input__slot .v-select__slot .v-select__selections .v-select__selection,
  ::v-deep .v-input__control .v-input__slot .v-select__slot .v-label,
  ::v-deep .v-input__control .v-input__slot .v-input__prepend-inner span {
    margin-right: 0;
    max-width: 100%;
  }
}

.select-color--readonly :deep(.v-input__slot) {
  cursor: default !important;
}

::v-deep {
  .v-icon--disabled {
    display: inline-flex !important;
  }
  .v-input__slot {
    cursor: pointer !important;
    min-height: 30px !important;
    background-color: var(--current-color) !important;
    fieldset {
      border: none !important;
    }
  }
  .v-input__control {
    min-height: unset !important;
  }
  .v-select__selection--comma {
    color: unset !important;
    margin: 0 !important;
  }
  .v-select__selection {
    color: var(--main-color) !important;
  }
  .v-select__selection,
  .v-select__selections,
  .no-data-label {
    font-size: v-bind(fontSize) !important;
  }
  .v-select__slot label {
    font-size: 14px !important;
  }
  .v-input__icon i {
    color: var(--main-color) !important;
    font-size: 20px;
  }
  .v-icon--disabled {
    display: none;
  }
  .v-input__icon--clear {
    margin-left: 10px;
  }
}
</style>
